import type {AfriexCurrencyCode, ArbitrageCheckInfo} from '@/types';

import {calculateArbitrageProfit} from './calculateArbitrageProfit';
import {checkPairwiseDiscrepancy} from './checkPairwiseDiscrepancy';
import {transformCycleToAmounts} from './transformCycleToAmounts';
import {transformToNegativeLog} from './transformToNegativeLog';

/**
 * This function detects arbitrage in a given rates matrix using Bellman-Ford algorithm
 * The following steps are taken:
 * 1. Transform the rates map to
 * 2. Transform the rates matrix to negative log values
 * 3. Relax edges V-1 times
 * 4. Check for negative-weight cycle after relaxing edges V-1 times
 * If a negative-weight cycle is found, then arbitrage is detected
 * @param matrix
 * @param currencySymbols
 * @returns
 */

export const detectArbitrage = (
  matrix: number[][],
  currencySymbols: AfriexCurrencyCode[],
  tolerancePercentage = 1,
): ArbitrageCheckInfo | null => {
  // Check for arbitrage in each matching currency pair
  const pairWiseDiscrepancy = checkPairwiseDiscrepancy(
    matrix,
    currencySymbols,
    tolerancePercentage,
  );

  const transformedMatrix = transformToNegativeLog(matrix);
  const numCurrencies = transformedMatrix.length;

  // Initialize distances to Infinity except for the start node
  const distances = new Array<number>(numCurrencies).fill(Infinity);
  distances[0] = numCurrencies - 1; // Start with the first currency

  // Create predecessor array to keep track of the path.
  const predecessor: number[] = new Array<number>(numCurrencies).fill(-1);

  // Relax edges V-1 times
  for (let i = 0; i < numCurrencies - 1; i++) {
    for (let src = 0; src < numCurrencies; src++) {
      for (let dest = 0; dest < numCurrencies; dest++) {
        if (distances[src] + transformedMatrix[src][dest] < distances[dest]) {
          distances[dest] = distances[src] + transformedMatrix[src][dest];
          predecessor[dest] = src;
        }
      }
    }
  }

  // Check for negative-weight cycle after full relaxation
  const cycles = [];
  for (let src = 0; src < numCurrencies; src++) {
    for (let dest = 0; dest < numCurrencies; dest++) {
      if (distances[src] + transformedMatrix[src][dest] < distances[dest]) {
        const startNode = dest;
        const printCycle: number[] = [dest, src];
        // Start from the source and go backwards until you see the source vertex again or any vertex that already exists in the printCycle array
        let predecessor_src = src;
        while (!printCycle.includes(predecessor[predecessor_src])) {
          printCycle.push(predecessor[predecessor_src]);
          predecessor_src = predecessor[predecessor_src];
        }
        printCycle.push(startNode);
        printCycle.reverse();
        const amounts = transformCycleToAmounts(printCycle, matrix);
        const profitPercentage = calculateArbitrageProfit({amounts});
        if (profitPercentage > tolerancePercentage) {
          cycles.push({
            amounts,
            currencies: printCycle.map((p) => currencySymbols[p]),
            printCycle,
          });
        }
      }
    }
  }
  return {cycles, pairWiseDiscrepancy};
};
